import type { Camera } from "./Camera"
import type { Scene } from "./Scene"

export interface MiniMapTheme {
  background: string
  viewportColor: string
  viewportStroke: string
}

const DEFAULT_THEME: MiniMapTheme = {
  background: "#111111",
  viewportColor: "rgba(255, 255, 255, 0.25)",
  viewportStroke: "rgba(255, 255, 255, 0.6)",
}
const MIN_FRAME_MS = 100 // ~10fps cap for minimap

export class MiniMapCanvas {
  private theme: MiniMapTheme
  private canvas!: HTMLCanvasElement
  private ctx!: CanvasRenderingContext2D
  private width = 0
  private height = 0
  private dpr = 1
  private scene: Scene | null = null
  private highlightedIds: Set<string> | null = null
  private lastRenderTime = 0
  private layerCanvas: HTMLCanvasElement | null = null
  private layerCtx: CanvasRenderingContext2D | null = null
  private layerDirty = true
  private mapScale = 1
  private mapOffsetX = 0
  private mapOffsetY = 0
  private resizeObserver: ResizeObserver | null = null
  private navigate: ((worldX: number, worldY: number) => void) | null = null
  private dragPointerId: number | null = null
  private dragOffsetX = 0
  private dragOffsetY = 0
  private viewportX = 0
  private viewportY = 0
  private viewportW = 0
  private viewportH = 0

  // Cached bounds — recomputed only when scene data changes
  private boundsMinX = 0
  private boundsMinY = 0
  private boundsW = 1
  private boundsH = 1

  constructor(theme: Partial<MiniMapTheme> = {}) {
    this.theme = { ...DEFAULT_THEME, ...theme }
  }

  setTheme(theme: Partial<MiniMapTheme>) {
    this.theme = { ...this.theme, ...theme }
    this.layerDirty = true
    this.lastRenderTime = 0
  }

  mount(container: HTMLElement) {
    this.canvas = document.createElement("canvas")
    this.canvas.className = "minimap-canvas"
    this.canvas.style.display = "block"
    this.canvas.style.cursor = "pointer"
    this.canvas.style.touchAction = "none"
    container.appendChild(this.canvas)
    this.ctx = this.canvas.getContext("2d")!
    this.dpr = devicePixelRatio || 1
    this.canvas.addEventListener("pointerdown", this.onPointerDown)
    this.canvas.addEventListener("pointermove", this.onPointerMove)
    this.canvas.addEventListener("pointerup", this.onPointerUp)
    this.canvas.addEventListener("pointercancel", this.onPointerUp)

    this.resizeObserver = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      this.dpr = devicePixelRatio || 1
      this.width = width
      this.height = height
      this.canvas.width = width * this.dpr
      this.canvas.height = height * this.dpr
      this.canvas.style.width = `${width}px`
      this.canvas.style.height = `${height}px`
      this.layerDirty = true
      this.lastRenderTime = 0
    })
    this.resizeObserver.observe(container)
  }

  setNavigateHandler(handler: (worldX: number, worldY: number) => void) {
    this.navigate = handler
  }

  setScene(scene: Scene) {
    this.scene = scene
    this.recomputeBounds()
    this.layerDirty = true
    this.lastRenderTime = 0
  }

  setHighlight(ids: Set<string> | null) {
    this.highlightedIds = ids
    this.layerDirty = true
    this.lastRenderTime = 0
  }

  private recomputeBounds() {
    const scene = this.scene
    if (!scene || scene.count === 0) {
      this.boundsMinX = 0
      this.boundsMinY = 0
      this.boundsW = 1
      this.boundsH = 1
      return
    }

    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (let i = 0; i < scene.count; i++) {
      const cx = scene.centerX(i)
      const cy = scene.centerY(i)
      if (cx < minX) minX = cx
      if (cy < minY) minY = cy
      if (cx > maxX) maxX = cx
      if (cy > maxY) maxY = cy
    }
    this.boundsMinX = minX
    this.boundsMinY = minY
    this.boundsW = maxX - minX || 1
    this.boundsH = maxY - minY || 1
  }

  private rebuildLayer() {
    const scene = this.scene
    if (!scene || this.width === 0 || this.height === 0) return

    if (!this.layerCanvas) {
      this.layerCanvas = document.createElement("canvas")
      this.layerCtx = this.layerCanvas.getContext("2d")!
    }

    this.layerCanvas.width = this.width * this.dpr
    this.layerCanvas.height = this.height * this.dpr
    const ctx = this.layerCtx!
    ctx.save()
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
    ctx.fillStyle = this.theme.background
    ctx.fillRect(0, 0, this.width, this.height)

    const pad = 10
    this.mapScale = Math.max(
      0.0001,
      Math.min(
        (this.width - pad * 2) / this.boundsW,
        (this.height - pad * 2) / this.boundsH,
      ),
    )
    this.mapOffsetX =
      (this.width - this.boundsW * this.mapScale) / 2 -
      this.boundsMinX * this.mapScale
    this.mapOffsetY =
      (this.height - this.boundsH * this.mapScale) / 2 -
      this.boundsMinY * this.mapScale

    const hasHighlight =
      this.highlightedIds !== null && this.highlightedIds.size > 0
    const byColor = new Map<string, { cx: number; cy: number; r: number }[]>()
    const dimByColor = new Map<
      string,
      { cx: number; cy: number; r: number }[]
    >()

    for (let i = 0; i < scene.count; i++) {
      const dimmed = hasHighlight && !this.highlightedIds!.has(scene.ids[i])
      const cx = scene.centerX(i) * this.mapScale + this.mapOffsetX
      const cy = scene.centerY(i) * this.mapScale + this.mapOffsetY
      const r = 3
      const target = dimmed ? dimByColor : byColor
      let batch = target.get(scene.colors[i])
      if (!batch) {
        batch = []
        target.set(scene.colors[i], batch)
      }
      batch.push({ cx, cy, r })
    }

    for (const [color, dots] of byColor) {
      ctx.fillStyle = color
      ctx.beginPath()
      for (const d of dots) {
        ctx.moveTo(d.cx + d.r, d.cy)
        ctx.arc(d.cx, d.cy, d.r, 0, Math.PI * 2)
      }
      ctx.fill()
    }

    if (dimByColor.size > 0) {
      ctx.globalAlpha = 0.1
      for (const [color, dots] of dimByColor) {
        ctx.fillStyle = color
        ctx.beginPath()
        for (const d of dots) {
          ctx.moveTo(d.cx + d.r, d.cy)
          ctx.arc(d.cx, d.cy, d.r, 0, Math.PI * 2)
        }
        ctx.fill()
      }
      ctx.globalAlpha = 1
    }

    ctx.restore()
    this.layerDirty = false
  }

  render(camera: Camera, canvasWidth: number, canvasHeight: number) {
    const scene = this.scene
    if (!scene || this.width === 0 || this.height === 0 || scene.count === 0) {
      return
    }

    // Throttle to ~10fps, except while direct minimap manipulation requests a frame.
    const now = performance.now()
    if (now - this.lastRenderTime < MIN_FRAME_MS) return
    this.lastRenderTime = now

    if (this.layerDirty) this.rebuildLayer()

    this.ctx.save()
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)

    if (this.layerCanvas) {
      this.ctx.drawImage(this.layerCanvas, 0, 0, this.width, this.height)
    } else {
      this.ctx.fillStyle = this.theme.background
      this.ctx.fillRect(0, 0, this.width, this.height)
    }

    const vp = camera.getVisibleRect(canvasWidth, canvasHeight)
    this.viewportX = vp.x * this.mapScale + this.mapOffsetX
    this.viewportY = vp.y * this.mapScale + this.mapOffsetY
    this.viewportW = vp.width * this.mapScale
    this.viewportH = vp.height * this.mapScale

    this.ctx.fillStyle = this.theme.viewportColor
    this.ctx.fillRect(
      this.viewportX,
      this.viewportY,
      this.viewportW,
      this.viewportH,
    )
    this.ctx.strokeStyle = this.theme.viewportStroke
    this.ctx.lineWidth = 1
    this.ctx.strokeRect(
      this.viewportX,
      this.viewportY,
      this.viewportW,
      this.viewportH,
    )

    this.ctx.restore()
  }

  private onPointerDown = (event: PointerEvent) => {
    if (event.pointerType === "mouse" && event.button !== 0) return
    if (!this.navigate || !this.scene || this.scene.count === 0) return
    event.preventDefault()
    event.stopPropagation()
    const point = this.localPoint(event)
    const world = this.mapToWorld(point.x, point.y)
    const inViewport =
      point.x >= this.viewportX &&
      point.x <= this.viewportX + this.viewportW &&
      point.y >= this.viewportY &&
      point.y <= this.viewportY + this.viewportH

    this.dragPointerId = event.pointerId
    this.canvas.setPointerCapture(event.pointerId)
    this.canvas.style.cursor = "grabbing"
    if (inViewport) {
      const center = this.mapToWorld(
        this.viewportX + this.viewportW / 2,
        this.viewportY + this.viewportH / 2,
      )
      this.dragOffsetX = world.x - center.x
      this.dragOffsetY = world.y - center.y
    } else {
      this.dragOffsetX = 0
      this.dragOffsetY = 0
      this.navigateTo(point.x, point.y)
    }
  }

  private onPointerMove = (event: PointerEvent) => {
    if (event.pointerId !== this.dragPointerId) return
    event.preventDefault()
    const point = this.localPoint(event)
    this.navigateTo(point.x, point.y)
  }

  private onPointerUp = (event: PointerEvent) => {
    if (event.pointerId !== this.dragPointerId) return
    if (this.canvas.hasPointerCapture(event.pointerId)) {
      this.canvas.releasePointerCapture(event.pointerId)
    }
    this.dragPointerId = null
    this.canvas.style.cursor = "pointer"
  }

  private navigateTo(mapX: number, mapY: number) {
    const world = this.mapToWorld(mapX, mapY)
    this.lastRenderTime = 0
    this.navigate?.(world.x - this.dragOffsetX, world.y - this.dragOffsetY)
  }

  private localPoint(event: PointerEvent) {
    const rect = this.canvas.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  private mapToWorld(x: number, y: number) {
    return {
      x: (x - this.mapOffsetX) / this.mapScale,
      y: (y - this.mapOffsetY) / this.mapScale,
    }
  }

  unmount() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.canvas.removeEventListener("pointerdown", this.onPointerDown)
    this.canvas.removeEventListener("pointermove", this.onPointerMove)
    this.canvas.removeEventListener("pointerup", this.onPointerUp)
    this.canvas.removeEventListener("pointercancel", this.onPointerUp)
    this.canvas.remove()
  }
}
